import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { History, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SubNav } from '@/components/ui/sub-nav';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

interface AuditEntry {
  id: string;
  userId: string;
  action: string;
  resource: string;
  resourceId: string;
  details: Record<string, unknown>;
  timestamp: string;
}

function AuditPage() {
  const { accessToken } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['audit'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/admin/audit`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch audit logs');
      return response.json();
    },
  });

  const entries: AuditEntry[] = data?.entries || [];

  const subNavItems = [
    { to: '/admin', label: 'Teams' },
    { to: '/admin/settings', label: 'Settings' },
    { to: '/admin/billing', label: 'Billing' },
    { to: '/admin/audit', label: 'Audit' },
  ];

  const getActionColor = (action: string) => {
    if (action.startsWith('create')) return 'bg-green-500/15 text-green-400';
    if (action.startsWith('delete')) return 'bg-red-500/15 text-red-400';
    if (action.startsWith('update')) return 'bg-blue-500/15 text-blue-400';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Audit Logs</h1>
        <p className="text-muted-foreground">Track all actions in your organization</p>
      </div>

      <SubNav items={subNavItems} />

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )}

      {entries.length === 0 && !isLoading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <History className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-foreground">No audit logs yet</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Actions will appear here as they happen
            </p>
          </CardContent>
        </Card>
      )}

      {entries.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Showing the last {entries.length} entries</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                      Timestamp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                      Action
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                      Resource
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={getActionColor(entry.action)}>{entry.action}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground">{entry.resource}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-muted-foreground">
                          {Object.keys(entry.details || {}).length > 0
                            ? JSON.stringify(entry.details).slice(0, 50)
                            : '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/audit')({
  component: AuditPage,
});
